import { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  Gift,
  Copy,
  Check,
  GraduationCap,
  UserRound,
  UsersRound,
  Link2,
  Loader2,
} from "lucide-react";
import { motion } from "motion/react";
import { useAuth } from "../../context/AuthContext";
import { app } from "../../firebase";

type ReferredPerson = {
  id: string;
  name: string;
  campaign: string;
};

const functions = getFunctions(app, "asia-southeast1");
const getMyReferralDetails = httpsCallable<
  void,
  { referrals: ReferredPerson[]; referredByName: string | null }
>(functions, "getMyReferralDetails");
const getOrCreateReferralCode = httpsCallable<void, { code?: string }>(
  functions,
  "getOrCreateReferralCode",
);

export default function ReferralPage() {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState<ReferredPerson[]>([]);
  const [referredByName, setReferredByName] = useState<string | null>(null);
  const [isReferralsLoading, setIsReferralsLoading] = useState(true);
  const [referralsError, setReferralsError] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [isReferralLinkLoading, setIsReferralLinkLoading] = useState(true);
  const [referralLinkMessage, setReferralLinkMessage] = useState("");

  useEffect(() => {
    let active = true;

    getMyReferralDetails()
      .then((result) => {
        if (active) {
          setReferrals(result.data.referrals || []);
          setReferredByName(result.data.referredByName || null);
        }
      })
      .catch((error) => {
        console.error("Failed to load referral details:", error);
        if (active) setReferralsError("Unable to load your referrals.");
      })
      .finally(() => {
        if (active) setIsReferralsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    getOrCreateReferralCode()
      .then((result) => {
        if (active) setReferralCode(String(result.data.code || ""));
      })
      .catch((error) => {
        console.error("Failed to load referral code:", error);
        if (active) {
          setReferralLinkMessage(
            "Your referral link is temporarily unavailable.",
          );
        }
      })
      .finally(() => {
        if (active) setIsReferralLinkLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const referralUrl = referralCode
    ? (() => {
        const url = new URL("/register", window.location.origin);
        url.searchParams.set("ref", referralCode);
        return url.toString();
      })()
    : "";

  const handleCopyReferralLink = async () => {
    if (!referralUrl) return;

    try {
      await navigator.clipboard.writeText(referralUrl);
      setReferralLinkMessage("Link copied");
    } catch (error) {
      console.error("Failed to copy referral link:", error);
      setReferralLinkMessage("Unable to copy the link. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="mx-auto flex max-w-5xl flex-col gap-6 p-4 lg:p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-[28px] font-black tracking-tight text-slate-900">
            Referrals
          </h1>

          <p className="mt-1 max-w-2xl text-[14px] text-slate-500">
            Invite your friends who are planning to study locally or overseas
            and earn rewards along the way.
          </p>
        </motion.div>

        {/* Referral Hero */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br
                     from-indigo-600 via-violet-600 to-purple-600
                     p-6 text-white shadow-lg shadow-violet-200/50
                     lg:p-8"
        >
          {/* Decorative circles */}
          <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-white/10" />
          <div className="absolute -bottom-24 right-32 h-52 w-52 rounded-full bg-pink-400/20" />

          <div className="relative z-10 grid gap-8 lg:grid-cols-[1.5fr_0.5fr]">
            <div>
              {/* Badge */}
              <div
                className="mb-5 inline-flex items-center gap-2 rounded-full
                              bg-white/15 px-3 py-1.5 text-xs font-semibold
                              backdrop-blur-sm"
              >
                <Gift className="h-4 w-4" />
                Referral Rewards
              </div>

              <h2 className="max-w-lg text-2xl font-black leading-tight lg:text-3xl">
                Invite friends.
                <br />
                Earn rewards together.
              </h2>

              <p className="mt-3 max-w-xl text-sm leading-6 text-white/75">
                Know someone thinking about their next step in education? Share
                your personal referral link with them.
              </p>

              {/* Referral link */}
              <div className="mt-6 flex max-w-xl flex-col gap-2 sm:flex-row">
                <div
                  className="flex min-w-0 flex-1 items-center gap-3 rounded-xl
                             border border-white/15 bg-white/10 px-4 py-3
                             backdrop-blur-sm"
                >
                  <Link2 className="h-4 w-4 shrink-0 text-white/70" />

                  <span className="truncate text-sm text-white/90">
                    {isReferralLinkLoading
                      ? "Preparing your referral link..."
                      : referralUrl || "Referral link unavailable"}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleCopyReferralLink}
                  disabled={isReferralLinkLoading || !referralUrl}
                  className="flex shrink-0 cursor-pointer items-center
                             justify-center gap-2 rounded-xl bg-white px-5 py-3
                             text-sm font-bold text-violet-700 shadow-sm
                             transition hover:bg-violet-50
                             disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isReferralLinkLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : referralLinkMessage === "Link copied" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {isReferralLinkLoading
                    ? "Preparing link"
                    : referralLinkMessage === "Link copied"
                      ? "Link copied"
                      : "Copy link"}
                </button>
              </div>

              {referralLinkMessage && (
                <p className="mt-2 text-xs text-white/75" role="status">
                  {referralLinkMessage}
                </p>
              )}
            </div>

            {/* Simple illustration */}
            <div className="hidden items-center justify-center lg:flex">
              <div className="relative flex h-40 w-40 items-center justify-center">
                <div className="absolute h-32 w-32 rounded-full bg-white/10" />
                <div className="absolute h-24 w-24 rounded-full bg-white/10" />

                <GraduationCap className="relative h-20 w-20 text-white" />

                <div
                  className="absolute -left-3 bottom-2 flex h-11 w-11
                             items-center justify-center rounded-2xl
                             bg-pink-400 shadow-lg"
                >
                  <UserRound className="h-5 w-5" />
                </div>

                <div
                  className="absolute -right-2 top-1 flex h-11 w-11
                             items-center justify-center rounded-2xl
                             bg-emerald-400 shadow-lg"
                >
                  <Check className="h-5 w-5" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Summary cards */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Referral count */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-5 rounded-2xl border
                       border-slate-100 bg-white p-5 shadow-sm"
          >
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center
                         rounded-2xl bg-violet-50 text-violet-600"
            >
              <UsersRound className="h-6 w-6" />
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500">
                Referred Friends
              </p>

              <p className="mt-0.5 text-3xl font-black text-slate-900">
                {referrals.length.toLocaleString()}
              </p>

              <p className="mt-0.5 text-xs text-slate-400">
                Total successful referrals
              </p>
            </div>
          </motion.div>

          {/* Referred by */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="flex items-center gap-5 rounded-2xl border
                       border-slate-100 bg-white p-5 shadow-sm"
          >
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center
                         rounded-2xl bg-emerald-50 text-emerald-600"
            >
              <UserRound className="h-6 w-6" />
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500">
                You were referred by
              </p>

              <p className="mt-1 font-bold text-slate-900">
                {isReferralsLoading
                  ? "Loading..."
                  : referredByName || "Not referred"}
              </p>

              <p className="mt-0.5 text-xs text-slate-400">
                Thanks for being part of our community.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Referral list */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="overflow-hidden rounded-3xl border border-slate-100
                     bg-white shadow-sm"
        >
          {/* List header */}
          <div
            className="flex items-center justify-between border-b
                       border-slate-100 px-5 py-5 lg:px-6"
          >
            <div>
              <h3 className="text-sm font-bold text-slate-900">
                Your Referred Friends
              </h3>

              <p className="mt-0.5 text-xs text-slate-400">
                Friends who joined using your referral link.
              </p>
            </div>

            {!isReferralsLoading && !referralsError && (
              <div
                className="rounded-full bg-violet-50 px-3 py-1.5
                           text-xs font-bold text-violet-600"
              >
                {referrals.length}{" "}
                {referrals.length === 1 ? "friend" : "friends"}
              </div>
            )}
          </div>

          {/* Loading */}
          {isReferralsLoading && (
            <div className="p-10 text-center text-sm text-slate-400">
              Loading referrals...
            </div>
          )}

          {/* Error */}
          {referralsError && (
            <div className="p-10 text-center text-sm text-red-500">
              {referralsError}
            </div>
          )}

          {/* Empty state */}
          {!isReferralsLoading && !referralsError && referrals.length === 0 && (
            <div className="flex flex-col items-center px-6 py-14 text-center">
              <div
                className="mb-4 flex h-14 w-14 items-center justify-center
                             rounded-2xl bg-violet-50"
              >
                <UsersRound className="h-6 w-6 text-violet-500" />
              </div>

              <p className="text-sm font-bold text-slate-800">
                No referrals yet
              </p>

              <p className="mt-1 max-w-xs text-xs leading-5 text-slate-400">
                Share your referral link with friends and they will appear here
                after joining.
              </p>
            </div>
          )}

          {/* Referral rows */}
          {!isReferralsLoading && !referralsError && referrals.length > 0 && (
            <div className="divide-y divide-slate-100">
              {referrals.map((referral, index) => (
                <div
                  key={referral.id}
                  className="group flex items-center gap-4 px-5 py-4
                               transition-colors hover:bg-slate-50/70
                               lg:px-6"
                >
                  {/* Number */}
                  <div
                    className="flex h-9 w-9 shrink-0 items-center
                                 justify-center rounded-full bg-slate-100
                                 text-xs font-bold text-slate-500"
                  >
                    {index + 1}
                  </div>

                  {/* Name */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800">
                      {referral.name}
                    </p>

                    <p className="mt-0.5 text-xs text-slate-400">
                      Referred friend
                    </p>
                  </div>

                  {/* Campaign */}
                  <div className="hidden sm:block">
                    <span
                      className="rounded-full bg-slate-100 px-3 py-1.5
                                   text-xs font-medium text-slate-500"
                    >
                      {referral.campaign || "Referral"}
                    </span>
                  </div>

                  {/* Status */}
                  <div
                    className="flex items-center gap-1.5 text-xs
                                 font-semibold text-emerald-600"
                  >
                    <span
                      className="flex h-5 w-5 items-center justify-center
                                   rounded-full bg-emerald-50"
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    Joined
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
